import { useState } from "react";
import { Service } from "@turizi/shared";
import { useTranslation } from "react-i18next";
import { useAddChildService, useRemoveChildService, useServices } from "@/hooks/api-hooks";
import { ServiceTitle } from "./services/service-title";
import { ActionIcon, Loader, Text, Group, Box, TextInput, Button, Modal } from "@mantine/core";
import { IconTrash, IconSearch, IconX, IconPlus } from "@tabler/icons-react";
import { TagIcon } from "@/lib/tag-utils";
import ServiceCardCatalog from "./service-card-catalog";
import { multiWordSearch } from "@/utils/search-utils";
import { ServiceToString } from "@/lib/models-utils";
import CardModal from "./card-modal";
import { SkeletonList } from "./snippets";

interface Props {
    service: Service;
}

export default function EditServiceChildren({ service }: Props) {
    const { t } = useTranslation();
    const addChildService = useAddChildService();
    const removeChildService = useRemoveChildService();
    const [search, setSearch] = useState('');
    const [modalOpened, setModalOpened] = useState(false);

    const { data: servicesData, isLoading } = useServices({}, { enabled: modalOpened });

    const existingChildrenIds = service.child_services?.map(s => s.id) || [];

    const availableServices = (servicesData?.data || []).filter(s => {
        if (s.id === service.id || existingChildrenIds.includes(s.id)) return false;
        if (!search) return true;
        return multiWordSearch(ServiceToString(s, t), search.toLowerCase());
    });

    const handleAdd = (childServiceId: number | string) => {
        addChildService.mutate({ serviceId: service.id, childServiceId });
        setModalOpened(false);
        setSearch('');
    };

    const handleRemove = (childServiceId: number | string) => {
        if (window.confirm(t('confirm_delete') as string)) {
            removeChildService.mutate({ serviceId: service.id, childServiceId });
        }
    };

    return (
        <div className="space-y-4">
            {service.child_services && service.child_services.length > 0 && (
                <div className="space-y-2">
                    {service.child_services.map((child: Service) => (
                        <div
                            key={child.id}
                            className="flex items-center justify-between gap-2"
                        >
                            <div className="flex-1 text-xs">
                                <ServiceCardCatalog
                                    service={child}
                                />
                            </div>
                            <ActionIcon
                                color="red"
                                variant="subtle"
                                onClick={() => handleRemove(child.id)}
                                loading={removeChildService.isPending}
                            >
                                <IconTrash size={16} />
                            </ActionIcon>
                        </div>
                    ))}
                </div>
            )}

            <Button
                variant="outline"
                size="xs"
                leftSection={<IconPlus size={16} />}
                onClick={() => setModalOpened(true)}
                color="gray"
            >
                {t('add_child_service')}
            </Button>

            <CardModal
                opened={modalOpened}
                onClose={() => {
                    setModalOpened(false);
                    setSearch('');
                }}
                header={t('add_child_service')}
            >
                <div className="space-y-4">
                    <TextInput
                        placeholder={t('search') + '...'}
                        rightSection={isLoading ? <Loader size={18} /> : (
                            search ? (
                                <ActionIcon size="sm" variant="transparent" onClick={() => setSearch('')}>
                                    <IconX size={16} />
                                </ActionIcon>
                            ) : <IconSearch size={18} />
                        )}
                        value={search}
                        onChange={(event) => setSearch(event.currentTarget.value)}
                        data-autofocus
                    />

                    {isLoading && <SkeletonList />}

                    {availableServices.map(item => (
                        <ServiceCardCatalog
                            key={item.id}
                            service={item}
                            hidePrices={true}
                            onSelect={() => handleAdd(item.id)}
                        />
                    ))}
                </div>
            </CardModal>
        </div>
    );
}
